import { ReactComponent as InfoIcon } from 'components/icons/info.svg';
import { ServiceTypes } from 'constants/ServiceTypes';
import { Ruleset } from 'enums/Ruleset';
import { IStrikeZoneDefinition } from 'interfaces/session/IStrikeZoneDefinition';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import { useInjection } from 'services/ioc/ioc.react';
import { ContextualDataStore } from 'stores/ContextualDataStore';
import { LineupStore } from 'stores/LineupStore';
import { OrganizationWarning } from '../OrganizationWarning/OrganizationWarning';
import { CustomSelect } from './CustomSelect';
import { GameRow, GameSettings, Text, Title } from './SelectGameSettingsPageStyles';

interface IProps {
  isScheduledGame: boolean;
  isPrivateGame: boolean;
  strikeZones: IStrikeZoneDefinition[];
  leagueError: boolean;
  levelError: boolean;
  setLeagueError: React.Dispatch<React.SetStateAction<boolean>>;
  setLevelError: React.Dispatch<React.SetStateAction<boolean>>;
  displayOrgWarning: boolean;
}

const SelectGameSettingsPage: React.FC<IProps> = observer((props) => {
  const { isScheduledGame, isPrivateGame, strikeZones, leagueError, levelError, setLeagueError, setLevelError, displayOrgWarning } = props;
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const contextualDataStore = useInjection<ContextualDataStore>(ServiceTypes.ContextualDataStore);

  const getLeagues = () => {
    if (!lineupStore.gameLevel.id) {
      return contextualDataStore.leagues;
    }

    const gameLevel = contextualDataStore.gameLevels.find((level) => level.id === lineupStore.gameLevel.id);

    if (gameLevel && gameLevel.leagueIds && gameLevel.leagueIds.length > 0) {
      return contextualDataStore.leagues.filter((league) => gameLevel.leagueIds?.includes(league.id || ''));
    }

    return [];
  };

  const getLevels = () => {
    if (!lineupStore.league.id) {
      return contextualDataStore.gameLevels;
    }

    const league = contextualDataStore.leagues.find((league) => league.id === lineupStore.league.id);

    if (league && league.levelIds && league.levelIds.length > 0) {
      return contextualDataStore.gameLevels.filter((level) => league.levelIds?.includes(level.id || ''));
    }

    return [];
  };

  const getLevelValue = () => {
    return lineupStore.gameLevel.name;
  };

  const getLeagueValue = () => {
    return lineupStore.league.name;
  };

  useEffect(() => {
    setLeagueError(lineupStore.league.id === '');
    setLevelError(lineupStore.gameLevel.id === '');

    return () => {
      setLeagueError(false);
      setLevelError(false);
    };
  }, []);

  return (
    <div>
      <GameSettings>
        <Title>Game settings</Title>
        {displayOrgWarning && <OrganizationWarning />}
        <GameRow>
          <CustomSelect
            placeholder='Organization'
            label='Select the organization which the teams belong to'
            value={lineupStore.owningOrganization.name}
            onSelectChange={(_1, _2, _3, newValueOption) => {
              lineupStore.setOwningOrganization({
                id: newValueOption?.option.value,
                name: newValueOption?.option.label,
              });
            }}
            isSorted
            emptyOption={{ value: '', label: '---' }}
            options={mappingHelpers.valuesToDropdownMap(contextualDataStore.owningOrganizations)}
            data-test='organization'
          />
          <CustomSelect
            placeholder='Level'
            label='For example AAA'
            isDisabled={isScheduledGame || isPrivateGame}
            value={getLevelValue()}
            onSelectChange={
              isScheduledGame
                ? () => {}
                : (_1, _2, _3, newValueOption) => {
                    lineupStore.setGameLevel({
                      id: newValueOption?.option.value,
                      name: newValueOption?.option.label,
                    });
                  }
            }
            isSorted
            options={mappingHelpers.valuesToDropdownMap(getLevels())}
            data-test='level'
            emptyOption={{ value: '', label: 'Undefined' }}
            disableClearable
            error={levelError}
          />
        </GameRow>
        <GameRow>
          <CustomSelect
            placeholder='League'
            label='Select the league for this game'
            isDisabled={isScheduledGame || isPrivateGame}
            value={getLeagueValue()}
            onSelectChange={
              isScheduledGame
                ? () => {}
                : (_1, _2, _3, newValueOption) => {
                    if (newValueOption) {
                      const league = getLeagues().find((league) => league.id === newValueOption.option.value);

                      if (league) {
                        lineupStore.setLeague(league);
                      } else {
                        lineupStore.setLeague({
                          id: newValueOption?.option.value,
                          name: newValueOption?.option.label,
                        });
                      }
                    } else {
                      lineupStore.setLeague({
                        id: undefined,
                        name: undefined,
                      });
                    }
                  }
            }
            isSorted
            options={mappingHelpers.valuesToDropdownMap(getLeagues())}
            data-test='league'
            emptyOption={{ value: '', label: 'Undefined' }}
            error={leagueError}
          />
          <CustomSelect
            placeholder='Ruleset'
            label='Select the ruleset'
            value={lineupStore.rulesets.find((r) => r.value === lineupStore.ruleset)?.label}
            onSelectChange={(_1, _2, _3, newValueOption) => {
              lineupStore.setRuleset(newValueOption?.option.value as Ruleset);
            }}
            isSorted
            options={lineupStore.rulesets.filter((r) => isPrivateGame || r.value !== Ruleset.scrimmage)}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            disableClearable
            data-test='ruleset'
          />
        </GameRow>
      </GameSettings>
      {isScheduledGame && (
        <div>
          <InfoIcon /> <Text>If the settings are not correct, please create a game and manually set the correct settings</Text>
        </div>
      )}
    </div>
  );
});

export default SelectGameSettingsPage;
